import type {ReactElement} from 'react';
import {AnimationStylesPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/animation-styles-panel/animation-styles-panel';
import {useAppSelector} from '@/hooks';
import {getCurrentPage} from '@Components/poster-editor/poster-editor-reducer';
import type {AnimationSpeed, SlideType} from '@PosterWhiteboard/classes/item-animation.class';
import {AnimationType} from '@PosterWhiteboard/classes/item-animation.class';
import {PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

export function PageIntroAnimationPanel(): ReactElement | null {
  const currentPageObject = useAppSelector(getCurrentPage);

  if (!currentPageObject) {
    throw new Error('Unable to show animation options since the current page is null');
  }

  if (Object.keys(currentPageObject.items.itemsHashMap).length === 0) {
    return null;
  }

  const onAnimationTypeModified = (animationType: AnimationType): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      throw new Error('Unable to change animation type since current page is undefined');
    }

    trackPosterBuilderGA4Events(GA4EventName.ANIMATION_DESIGN_ENTRANCE, {[GA4EventParamName.ANIMATION_TYPE]: animationType});
    currentPage.introAnimation.updateFromObject(
      {
        type: animationType,
        direction: animationType === AnimationType.ZOOM ? PositionType.LEFT : PositionType.RIGHT,
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: true}
    );
  };
  const onAnimationSpeedModified = (undoable: boolean, replayPosterOnChange: boolean, speed: AnimationSpeed): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      throw new Error('Unable to change animation type since current page is undefined');
    }

    currentPage.introAnimation.updateFromObject(
      {
        speed: speed,
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: replayPosterOnChange, undoable: undoable}
    );
  };
  const onAnimationDirectionModified = (direction: SlideType | PositionType): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      throw new Error('Unable to change animation type since current page is undefined');
    }

    currentPage.introAnimation.updateFromObject(
      {
        direction: direction,
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: true}
    );
  };

  const getAnimations = (): AnimationType[] => {
    return [
      AnimationType.NONE,
      AnimationType.BLOCK,
      AnimationType.BOUNCE,
      AnimationType.FADE,
      AnimationType.JELLO,
      AnimationType.PAN,
      AnimationType.POP,
      AnimationType.ROTATE,
      AnimationType.SCALE,
      AnimationType.SLIDE,
      AnimationType.TUMBLE,
      AnimationType.WIPE,
      AnimationType.PEEK,
      AnimationType.ZOOM,
    ];
  };

  return (
    <AnimationStylesPanel
      selectedAnimation={currentPageObject.introAnimation.type}
      onAnimationTypeModified={onAnimationTypeModified}
      onAnimationSpeedModified={onAnimationSpeedModified}
      onAnimationDirectionModified={onAnimationDirectionModified}
      speed={currentPageObject.introAnimation.speed}
      direction={currentPageObject.introAnimation.direction}
      animations={getAnimations()}
      showInGrid={true}
    />
  );
}
